import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { localeToCountryCode } from '@/types/locales'

import { useLocale } from '@/context/localeContext'

import { useAuthInfo } from '@/lib/hooks/useAuthInfo'
import { isValidCheckoutCountry } from '@/lib/hooks/useCheckoutCountry'
import { getProductsAvailability } from '@/lib/payment-service'

import type { ProductAvailability } from '@/clients/payment-service'

type ProductAvailabilityMap = { [key: string]: ProductAvailability }

interface ProductAvailabilityContextType {
  productAvailability: ProductAvailabilityMap
  isLoading: boolean
  error: Error | null
  refreshProductAvailability: () => Promise<void>
}

const ProductAvailabilityContext = createContext<ProductAvailabilityContextType>({
  productAvailability: {},
  isLoading: true,
  error: null,
  refreshProductAvailability: async () => {},
})

interface ProductAvailabilityProviderProps {
  children: React.ReactNode
}

export const ProductAvailabilityProvider = ({ children }: ProductAvailabilityProviderProps) => {
  const { authenticatedUser, isLoading: isAuthLoading } = useAuthInfo()
  const { currentLocale } = useLocale()
  const [productAvailability, setProductAvailability] = useState<ProductAvailabilityMap>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchProductAvailability = useCallback(async () => {
    setIsLoading(true)

    if (isAuthLoading) {
      return
    }

    if (!authenticatedUser) {
      setIsLoading(false)
      return
    }

    try {
      setError(null)
      // Availability is only meaningful for countries we can sell in.
      const countryCodeString = localeToCountryCode(currentLocale)
      const countryCode = isValidCheckoutCountry(countryCodeString) ? countryCodeString : undefined
      const availability = await getProductsAvailability(authenticatedUser, countryCode)
      setProductAvailability(availability)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch product availability'))
      setProductAvailability({})
    } finally {
      setIsLoading(false)
    }
  }, [authenticatedUser, isAuthLoading, currentLocale])

  useEffect(() => {
    void fetchProductAvailability()
  }, [fetchProductAvailability])

  const contextValue = useMemo(
    () => ({
      productAvailability,
      isLoading,
      error,
      refreshProductAvailability: fetchProductAvailability,
    }),
    [productAvailability, isLoading, error, fetchProductAvailability],
  )

  return <ProductAvailabilityContext.Provider value={contextValue}>{children}</ProductAvailabilityContext.Provider>
}

export const useProductAvailability = () => {
  const context = React.useContext(ProductAvailabilityContext)
  if (context === undefined) {
    throw new Error('useProductAvailability must be used within a ProductAvailabilityProvider')
  }
  return context
}
